import { Controller } from "@hotwired/stimulus"

// Park a scrollable container at its end, and keep it there while new messages arrive — for
// the conversation panes, which are read newest-last even though a thread is written
// oldest-first.
//
// Registered in *both* bundles (controllers/index.js and portal.js) from one file. The portal
// deliberately shares no partials, helpers or styling with the Bootstrap side, but this carries
// none of those: it sets `scrollTop`. Two copies of that would be two things to fix.
//
// JS rather than the CSS `flex-direction: column-reverse` trick, which gets the same initial
// scroll position for free but only by making the DOM order the reverse of the visual order. A
// screen reader would then read the thread backwards, which is a real cost for a saved five
// lines.
//
// **`connect()` alone is not enough, and that was a real bug.** Posting a reply re-renders the
// overlay's frame, and turbo_overlay's own comment says what that does: "Turbo's default frame
// replacement swaps the `<dialog>` node entirely; the new dialog has no `open` attribute and is
// detached from the top layer" until the gem's controller re-opens it. A closed `<dialog>` is
// `display: none`, so at connect time this element has no layout at all — `scrollHeight` is 0,
// `scrollTop = 0` is a no-op, and the reply the member just sent renders with its top edge
// tucked under the composer. The `ResizeObserver` is what catches the moment the dialog opens
// and the pane finally has a height; it also covers a font or image settling afterwards, and
// the composer's textarea growing under the thread.
//
// **Pinned only while the reader is already at the end.** Someone who has scrolled up to
// re-read something while typing must not be yanked back down every time the textarea grows a
// line.
export default class extends Controller {
  connect() {
    this.pinned = true

    this.trackScroll = () => { this.pinned = this.atEnd }
    this.element.addEventListener("scroll", this.trackScroll, { passive: true })

    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(() => { if (this.pinned) this.scroll() })
      this.observer.observe(this.element)
    }

    this.scroll()
    // A pass after layout settles, for the case where this element *does* have a height on
    // connect: the thread's own height depends on the wrapping of messages that have only just
    // been inserted, so the first `scrollHeight` can be short by a line or two.
    requestAnimationFrame(() => { if (this.pinned) this.scroll() })
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.trackScroll)
    this.observer?.disconnect()
  }

  scroll() {
    this.element.scrollTop = this.element.scrollHeight
  }

  // A few pixels of tolerance: fractional layout and momentum scrolling both leave a
  // scrolled-to-the-bottom container a hair short of the exact figure.
  get atEnd() {
    const { scrollHeight, scrollTop, clientHeight } = this.element
    return scrollHeight - scrollTop - clientHeight <= 4
  }
}
